/*** 长连接方法
 * @description 该类会先判断浏览器可用的长连接方案，默认使用websocket，其不可用时会自动切换为sse
 * 注意：sse无法监听到关闭事件，只能主动读取其状态；sse无法发送消息；
 *
 * 使用示例：
 * const rpc = new LongRpc('http://xxx.net');
 * // const rpc = new LongRpc('http://xxx.net',{method:'EventSource'}); // 使用sse时
 *
 * rpc.addEventListener('open',function(){
 *  // 连接开启
 * });
 *
 * rpc.addEventListener('disabled',function(){
 *  // websocket，sse均不可用的情况
 * });
 * // 其它还有close，error事件
 * rpc.addEventListener('message',function(data){
 *  // 接收到消息时
 * });
 *
 * //rpc.removeListener('open',fun); // 移除该订阅者（addEventListener时使用的函数）
 *
 * rpc.start(); // 开始连接
 *
 * rpc.send({id:'sfd'}).catch(info=>{
 *  if(info==='disabled') console.log('sse情况，不可发送消息');
 *  else console.log('非open状态',info);
 * })
 *
 * rpc.close();
*/
export class LongRpc{
  static METHODS = ['WebSocket', 'EventSource'];
  static EVENT_LIST = ['open','message','error','close','disabled'];
  // 判断浏览器可用的方法列表
  static getValidMethods() {
    let _ms = [];
    LongRpc.METHODS.forEach(m => {
      if (m in window) _ms.push(m);
    });
    return _ms;
  }
  //url: 连接的地址；param: {method:'WebSocket'}
  constructor(url, param) {
    let _param = param || {};
    const defaultConfig = {
      method: LongRpc.METHODS[0], // 默认使用 WebSocket
    };
    // 最终配置
    const config = { ...defaultConfig, ..._param };

    this._url = url;
    this._method = ''; // 当前使用的连接方法
    // 初始化订阅者
    let _map = {};
    LongRpc.EVENT_LIST.forEach(ename => {
      _map[ename] = [];
    })
    this._subscribeMap = _map; // 订阅者数据

    const activeMethods = LongRpc.getValidMethods(); // 浏览器可用的方法列表
    const configMethod = config.method;

    this._validMethods = activeMethods; // 可用的方法列表
    if (!LongRpc.METHODS.includes(configMethod)) {
      console.warn(`invalid method "${configMethod}", please use one of ${LongRpc.METHODS}`);
      return;
    }
    if (activeMethods.length > 0) {
      // 如果指定的方法不可用则使用另一个
      if (activeMethods.includes(configMethod)) this._method = configMethod;
      else this._method = activeMethods[0];
    } else {
      console.warn('[websocket与sse均不可用]');
    }

    // 配置保存
    this._config = config;
    this._rpcCase = null; // 连接实例
  }
  start() {
    if (this._method === LongRpc.METHODS[0]) {
      this.initWebSocket();
      return true;
    } else if(this._method === LongRpc.METHODS[1]){
      this.initSse();
      return true;
    } else {
      console.warn('[websocket与sse均不可用]');
      // 触发disabled事件
      this._publishMessage(LongRpc.EVENT_LIST[4]);
      return false;
    }
  }
  // websocket的处理
  initWebSocket() {
    const envList = LongRpc.EVENT_LIST;
    const rpc = new WebSocket(this._url);
    // 将WebSocket实例添加到全局数组
    if (!window._globalWebsockets) {
      window._globalWebsockets = [];
    }
    window._globalWebsockets.push(rpc);
    rpc.onopen = ()=>{
      this._publishMessage(envList[0]);
    }
    rpc.onmessage = (evt)=>{
      const receivedMsg = evt.data;
      this._publishMessage(envList[1],receivedMsg);
    };

    rpc.onerror = (er)=>{
      console.error('[connect error]', er);
      this._publishMessage(envList[2]);
    };
    rpc.onclose = ()=>{
      this._publishMessage(envList[3]);
    };

    this._rpcCase = rpc;
  }
  initSse() {
    const envList = LongRpc.EVENT_LIST;
    const sse = new EventSource(this._url);
    // sse.readyState 0:'connecting' 1:'open' 2:'closed'
    sse.onopen = () => {
      this._publishMessage(envList[0]);
    }
    sse.onmessage = (e) => {
      const data = e.data;
      this._publishMessage(envList[1],data);
    }
    sse.onerror = () => {
      this._publishMessage(envList[2]);
    }
    // sse无法监听到关闭事件
    this._rpcCase = sse;
  }
  send(data) {
    const _state = this.getConnectState();

    if (_state === 'open') {
      if (this._method === LongRpc.METHODS[0]) {
        this._rpcCase.send(data);
        return Promise.resolve('success');
      }
      else {
        //console.error('[sse为单向，不可发送消息]');
        return Promise.reject('disabled');
      }
    } else {
      //console.info(`[当前状态为${_state}，不可通信]`);
      return Promise.reject(_state);;
    }
  }
  close() { this._rpcCase.close(); }
  // 获取连接状态
  getConnectState() {
    const state = this._rpcCase.readyState;
    let _msg = '';
    switch (state) {
      case 0:
        _msg = 'connecting';
        break;
      case 1:
        _msg = 'open';
        break;
      case 2:
        // websocket的状态2为关闭中，处理为与sse一致
        _msg = 'closed';
        break;
      case 3:
        _msg = 'closed';
        break;
      default:
        _msg = 'unknow';
    }
    return _msg;
  }
  getRpcCase() { return this._rpcCase; }
  getMethod() { return this._method; }
  // 添加订阅者
  addEventListener(eventName, fn) {
    if (!LongRpc.EVENT_LIST.includes(eventName)) {
      console.error('invalid event '+eventName);
      return false;
    }

    if (typeof fn !== 'function') {
      console.error('param is not a function');
      return false;
    }
    const events = this._subscribeMap[eventName];
    if (events.includes[fn]) {
      console.warn('function exited in subscribeList');
      return false;
    }
    events.push(fn);
    return true;
  }
  // 移除订阅
  removeListener(eventName, fn) {
    const envList = this._subscribeMap[eventName];
    const ix = envList.indexOf(fn);
    if (ix !== -1) {
      envList.splice(ix, 1);
      return true;
    } else {
      console.warn('faild to remove event:'+eventName);
      return false;
    }
  }
  // 执行订阅者方法
  _publishMessage(message, data) {
    const envs = this._subscribeMap[message];
    envs.forEach(fn => {
      try { fn(data) }
      catch (er) {
        console.error('faild to implement the function', fn);
      }
    });
  }
}
